import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    PROCESS_EXECUTE_INTERVAL_UNITS,
    type ProcessExecuteIntervalType,
    type ProcessExecuteIntervalUnit,
    type ProcessIntervalAnchor,
} from '@econtractor/types';

type IntervalMode = 'instant' | 'byCore' | 'after';
type AmountUnit = Exclude<ProcessExecuteIntervalUnit, 'byCoreTask'>;

const AMOUNT_UNITS = PROCESS_EXECUTE_INTERVAL_UNITS.filter((unit): unit is AmountUnit => unit !== 'byCoreTask');

const intervalMode = (value: ProcessExecuteIntervalType | null): IntervalMode => {
    if (value?.unit === 'byCoreTask') return 'byCore';
    if (value == null || value.value === 0) return 'instant';
    return 'after';
};

const amountUnit = (value: ProcessExecuteIntervalType | null): AmountUnit => (
    value?.unit && value.unit !== 'byCoreTask' ? value.unit : 'day'
);

const intervalChoiceLabel = (title: string, hint: string) => (
    <span>
        {title}
        <span className="d-block text-muted fs-12 fw-normal">{hint}</span>
    </span>
);

interface ProcessDiagramIntervalDialogProps {
    show: boolean;
    value: ProcessExecuteIntervalType | null;
    anchor: ProcessIntervalAnchor | null;
    onHide: () => void;
    onSave: (interval: ProcessExecuteIntervalType | null, anchor: ProcessIntervalAnchor | null) => void;
}

const ProcessDiagramIntervalDialog = ({ show, value, anchor, onHide, onSave }: ProcessDiagramIntervalDialogProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const [mode, setMode] = useState<IntervalMode>(() => intervalMode(value));
    const [unit, setUnit] = useState<AmountUnit>(() => amountUnit(value));
    const [amount, setAmount] = useState(value && value.unit !== 'byCoreTask' ? value.value : 1);
    const [fromDeadline, setFromDeadline] = useState(anchor === 'deadline');

    useEffect(() => {
        if (!show) return;
        setMode(intervalMode(value));
        setUnit(amountUnit(value));
        setAmount(value && value.unit !== 'byCoreTask' && value.value > 0 ? value.value : 1);
        setFromDeadline(anchor === 'deadline');
    }, [show, value, anchor]);

    const save = () => {
        const savedAnchor = mode === 'byCore' || !fromDeadline ? null : 'deadline';
        if (mode === 'instant') {
            onSave(null, savedAnchor);
            return;
        }
        if (mode === 'byCore') {
            onSave({ value: 0, unit: 'byCoreTask' }, null);
            return;
        }
        const next = Math.max(0, Math.round(Number(amount) || 0));
        onSave(next === 0 ? null : { value: next, unit }, savedAnchor);
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('processDiagram.intervalTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Check
                    type="radio"
                    name="process-interval-mode"
                    id="process-interval-instant"
                    className="mb-3"
                    label={intervalChoiceLabel(
                        t('processDiagram.intervalInstant'),
                        t('processDiagram.intervalInstantHint'),
                    )}
                    checked={mode === 'instant'}
                    onChange={() => setMode('instant')}
                />
                <Form.Check
                    type="radio"
                    name="process-interval-mode"
                    id="process-interval-by-core"
                    className="mb-3"
                    label={intervalChoiceLabel(
                        t('processDiagram.intervalByCore'),
                        t('processDiagram.intervalByCoreHint'),
                    )}
                    checked={mode === 'byCore'}
                    onChange={() => setMode('byCore')}
                />
                <Form.Check
                    type="radio"
                    name="process-interval-mode"
                    id="process-interval-after"
                    className="mb-2"
                    label={intervalChoiceLabel(
                        t('processDiagram.intervalAfter'),
                        t('processDiagram.intervalAfterHint'),
                    )}
                    checked={mode === 'after'}
                    onChange={() => setMode('after')}
                />
                {mode === 'after' ? (
                    <div className="d-flex gap-2 ps-4">
                        <Form.Control
                            type="number"
                            min={0}
                            step={1}
                            value={amount}
                            onChange={(event) => setAmount(Number(event.target.value))}
                        />
                        <Form.Select
                            value={unit}
                            onChange={(event) => setUnit(event.target.value as AmountUnit)}
                        >
                            {AMOUNT_UNITS.map((item) => (
                                <option key={item} value={item}>
                                    {t(`processDiagram.units.${item}`)}
                                </option>
                            ))}
                        </Form.Select>
                    </div>
                ) : null}
                {mode !== 'byCore' ? (
                    <div className="mt-3 px-2 py-2 border rounded">
                        <div className="fw-medium text-body">{t('processDiagram.intervalAnchorTitle')}</div>
                        <div className="text-muted fs-12 mb-2">{t('processDiagram.intervalAnchorHint')}</div>
                        <div className="d-flex align-items-center gap-2">
                            <span className={`fs-12 ${fromDeadline ? 'text-muted' : 'fw-medium text-primary'}`}>
                                {t('processDiagram.intervalFromAccept')}
                            </span>
                            <Form.Check
                                type="switch"
                                id="process-interval-anchor"
                                className="mb-0 mx-auto"
                                checked={fromDeadline}
                                onChange={(event) => setFromDeadline(event.target.checked)}
                                aria-label={fromDeadline
                                    ? t('processDiagram.intervalFromDeadline')
                                    : t('processDiagram.intervalFromAccept')}
                            />
                            <span className={`fs-12 text-end ${fromDeadline ? 'fw-medium text-primary' : 'text-muted'}`}>
                                {t('processDiagram.intervalFromDeadline')}
                            </span>
                        </div>
                    </div>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide}>
                    {t('common:buttons.cancel')}
                </Button>
                <Button variant="primary" onClick={save}>
                    {t('common:buttons.save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ProcessDiagramIntervalDialog;
